import React, { useState, useEffect } from 'react';
import UploadPage from './pages/UploadPage';
import ResultPage from './pages/ResultPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import PricingPage from './pages/PricingPage';
import BatchResultPage from './pages/BatchResultPage';
import { getToken, getUser, logout, refreshMe, resendVerification } from './services/auth';
import { bulletinFiscal, bulletinFiscalDuLot } from './services/bulletinFiscal';
import './App.css';

export default function App() {
  const [user, setUser] = useState(getUser());
  const [page, setPage] = useState('upload');
  const [result, setResult] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [filename, setFilename] = useState('');
  const [notice, setNotice] = useState(null);
  // Grandeurs fiscales du dernier bulletin analyse. Conservees au retour sur la
  // page d'analyse, sinon le rapprochement avec l'avis exigerait une ressaisie
  // du net imposable : la page de resultat a deja ete quittee a ce moment-la.
  const [bulletinFiscalCourant, setBulletinFiscalCourant] = useState(null);

  /**
   * Relit le profil jusqu'à ce que le passage en Pro soit enregistré.
   *
   * Le webhook Stripe arrive en général dans la seconde, mais rien ne le
   * garantit : quatre tentatives espacées couvrent le cas courant sans
   * s'acharner, et l'utilisateur peut de toute façon recharger la page.
   */
  const attendreLePlanPro = () => {
    const delais = [1500, 4000, 8000, 15000];
    delais.forEach((delai) => {
      setTimeout(() => {
        refreshMe().then((u) => {
          if (u?.isPro) setUser(u);
        }).catch(() => {});
      }, delai);
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Retour de Stripe. Le paramètre est retiré de l'adresse : sans cela, chaque
    // rechargement rejouait le message et ramenait sur le tableau de bord.
    const upgrade = params.get('upgrade');
    if (upgrade === 'success') {
      setNotice({
        type: 'success',
        text: 'Abonnement Pro activé. Vos analyses sont désormais illimitées.',
      });
      setPage('dashboard');
      // Stripe renvoie l'utilisateur avant d'avoir notifié le serveur : le plan
      // n'est donc pas encore à jour à cet instant. Sans ces relectures, le
      // tableau de bord afficherait FREE juste après un paiement réussi.
      attendreLePlanPro();
    } else if (upgrade === 'cancelled') {
      setNotice({
        type: 'error',
        text: "Paiement annulé — aucun montant n'a été prélevé.",
      });
      setPage('pricing');
    }

    // Retour du portail de facturation Stripe.
    if (params.get('page') === 'dashboard') {
      setPage('dashboard');
    }

    if (upgrade || params.get('page')) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('session') === 'expiree') {
      setNotice({ type: 'error', text: 'Session expirée. Reconnectez-vous pour continuer.' });
      window.history.replaceState({}, '', window.location.pathname);
    }

    const verified = params.get('verified');
    if (verified === '1') {
      setNotice({ type: 'success', text: 'Adresse email confirmée. Vous pouvez lancer vos analyses.' });
    } else if (verified === '0') {
      setNotice({ type: 'error', text: 'Lien invalide ou expiré. Demandez un nouvel email de confirmation.' });
    }
    if (verified) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Le compteur de documents et le statut de verification changent cote serveur
    // (analyse, clic sur le lien de confirmation, parfois depuis un autre onglet).
    if (getToken()) {
      refreshMe().then(setUser).catch(() => {});
    }
  }, []);

  // Le lien de confirmation est souvent ouvert dans un autre onglet : on resynchronise
  // au retour sur celui-ci pour que le bandeau disparaisse sans rechargement manuel.
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState === 'visible' && getToken()) {
        refreshMe().then(setUser).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setPage('upload');
  };

  const handleLogout = () => {
    logout();
    setUser(null);
    setPage('upload');
    setResult(null);
    setBatchResult(null);
    setBulletinFiscalCourant(null);
  };

  const handleResult = (data, name) => {
    setResult(data);
    setFilename(name);
    setBulletinFiscalCourant(bulletinFiscal(data?.documentInfo, name));
    setPage('result');
    refreshMe().then(setUser).catch(() => {});
    if (data.remainingDocuments === 0 && !data.isPro) {
      setTimeout(() => setPage('pricing'), 3000);
    }
  };

  const handleBatchResult = (data) => {
    setBatchResult(data);
    setBulletinFiscalCourant(bulletinFiscalDuLot(data));
    setPage('batch-result');
    refreshMe().then(setUser).catch(() => {});
  };

  const handleReset = () => { setResult(null); setBatchResult(null); setPage('upload'); };

  if (!user && page === 'upload') {
    // Le bandeau doit figurer ici aussi : c'est l'ecran ou atterrit une session
    // expiree, et sans lui l'utilisateur serait deconnecte sans explication.
    return (
      <div className="app">
        <Header user={user} onLogout={handleLogout} onNav={setPage} />
        {notice && <Notice notice={notice} onClose={() => setNotice(null)} />}
        <LoginPage onLogin={handleLogin} onRegister={() => setPage('register')} />
      </div>
    );
  }
  if (page === 'register') {
    return <div className="app"><Header user={user} onLogout={handleLogout} onNav={setPage} /><RegisterPage onLogin={handleLogin} onBack={() => setPage('upload')} /></div>;
  }

  return (
    <div className="app">
      <Header user={user} onLogout={handleLogout} onNav={setPage} currentPage={page} />
      {notice && <Notice notice={notice} onClose={() => setNotice(null)} />}
      {user && !user.emailVerified && <VerifyBanner email={user.email} />}
      {page === 'upload' && <UploadPage onResult={handleResult} onBatchResult={handleBatchResult} user={user} bulletinFiscal={bulletinFiscalCourant} onUpgrade={() => setPage('pricing')} />}
      {page === 'result' && <ResultPage result={result} filename={filename} onReset={handleReset} onUpgrade={() => setPage('pricing')} />}
      {page === 'batch-result' && <BatchResultPage batchResult={batchResult} onReset={handleReset} onUpgrade={() => setPage('pricing')} />}
      {page === 'dashboard' && <DashboardPage user={user} onUpgrade={() => setPage('pricing')} onAnalyze={() => setPage('upload')} />}
      {page === 'pricing' && <PricingPage user={user} onBack={() => setPage('upload')} />}
      <SiteFooter />
    </div>
  );
}

// Liens vers les pages editoriales, servies en HTML statique hors de l'application.
function SiteFooter() {
  return (
    <footer className="app-footer">
      <nav>
        <a href="/fraude-fiche-de-paie/">Fraude à la fiche de paie</a>
        <a href="/verifier-fiche-de-paie-locataire/">Vérifier un dossier locataire</a>
        <a href="/faux-bulletin-de-salaire/">Reconnaître un faux bulletin</a>
        <a href="/contact/">Contact</a>
      </nav>
    </footer>
  );
}

function Notice({ notice, onClose }) {
  return (
    <div className={`notice notice-${notice.type}`}>
      <span>{notice.text}</span>
      <button className="notice-close" onClick={onClose} aria-label="Fermer">×</button>
    </div>
  );
}

function VerifyBanner({ email }) {
  const [state, setState] = useState('idle');

  const send = async () => {
    setState('sending');
    try {
      await resendVerification();
      setState('sent');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="verify-banner">
      <span className="verify-icon">✉</span>
      <div className="verify-text">
        <strong>Confirmez votre adresse email</strong>
        <span>Un lien a été envoyé à {email}. L'analyse sera débloquée dès la confirmation.</span>
      </div>
      {state === 'sent'
        ? <span className="verify-sent">Email renvoyé</span>
        : <button className="verify-btn" onClick={send} disabled={state === 'sending'}>
            {state === 'sending' ? 'Envoi…' : state === 'error' ? 'Réessayer' : 'Renvoyer l’email'}
          </button>}
    </div>
  );
}

function Header({ user, onLogout, onNav, currentPage }) {
  return (
    <header className="header">
      <div className="header-inner">
        <div className="logo" onClick={() => onNav('upload')} style={{cursor:'pointer'}}>
          <span className="logo-icon">⬡</span>
          <span className="logo-text">FRAUD<span className="logo-accent">DETECT</span></span>
        </div>
        <nav className="header-nav">
          {user && <>
            <button className={`nav-link ${currentPage==='upload'?'active':''}`} onClick={() => onNav('upload')}>Analyser</button>
            <button className={`nav-link ${currentPage==='dashboard'?'active':''}`} onClick={() => onNav('dashboard')}>Dashboard</button>
            <button className={`nav-link ${currentPage==='pricing'?'active':''}`} onClick={() => onNav('pricing')}>Tarifs</button>
          </>}
        </nav>
        <div className="header-right">
          {user ? (
            <div className="header-user">
              <span className={`plan-badge ${user.isPro ? 'pro' : 'free'}`}>{user.isPro ? 'PRO' : 'FREE'}</span>
              {!user.isPro && user.remainingDocuments >= 0 && <span className="docs-left">{user.remainingDocuments} doc{user.remainingDocuments !== 1 ? 's' : ''} restant{user.remainingDocuments !== 1 ? 's' : ''}</span>}
              <span className="user-email">{user.email}</span>
              <button className="logout-btn" onClick={onLogout}>Déconnexion</button>
            </div>
          ) : (
            <div className="badge-dot-wrap"><span className="badge-dot"></span><span style={{fontSize:12,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>Bulletin de salaire · France</span></div>
          )}
        </div>
      </div>
    </header>
  );
}
